import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Sparkles, MessageSquare, FileText, Shield, 
  Send, X, ChevronDown, Bot, HelpCircle,
  CheckCircle2, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';

const InsuranceCopilotSlide = ({ isActive = true }) => {
  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-[80px] translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/50 rounded-full blur-[80px] -translate-x-1/2 translate-y-1/2" />

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-8 relative z-10">
            {/* Left Side: Context */}
            <div className="w-[40%] flex flex-col justify-center space-y-8">
                
                {/* Agent Header (Inline) */}
                <div className="flex items-center gap-4 bg-white/40 backdrop-blur-md p-2 pr-6 rounded-full border border-white/20 shadow-lg w-fit mb-8">
                    <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-sm">
                         <img src="/assets/amanda.png" alt="Amanda" className="w-full h-full object-cover" />
                    </div>
                    <div className="text-left">
                        <h3 className="text-lg font-bold text-slate-900 leading-none">Amanda</h3>
                        <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">AI Insurance Co-Pilot</p>
                    </div>
                </div>

                <div className="space-y-6">
                    <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
                        Ask AI to get quick answers.
                    </h2>
                    
                    <div className="space-y-6">
                        <div className="flex gap-4">
                            <div className="mt-1 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-600">
                                <FileText className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-lg font-bold text-slate-900 mb-1">Plain English Summaries</h4>
                                <p className="text-slate-600 leading-relaxed text-sm">
                                    Amanda analyzes raw EDI files, payer policies, and your Asha PMS data (booked procedures) to generate a clear, concise summary. No more decoding cryptic codes.
                                </p>
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <div className="mt-1 w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 text-purple-600">
                                <MessageSquare className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="text-lg font-bold text-slate-900 mb-1">Ask AI Anything</h4>
                                <p className="text-slate-600 leading-relaxed text-sm">
                                    Have a specific question? Just ask. Amanda reads through the entire benefits document instantly to find answers about frequencies, limitations, and clauses.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side: Graphic */}
            <div className="w-[60%] h-full flex items-center justify-center relative">
                
                {/* Main Dashboard Panel */}
                <motion.div 
                    initial={{ y: 40, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    className="w-full h-[600px] bg-slate-50 rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col relative"
                >
                    {/* Header */}
                    <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Shield className="w-5 h-5 text-blue-600" />
                            <span className="font-bold text-slate-900">Benefits Summary</span>
                             <div className="ml-4 flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full px-4 py-2 shadow-lg shadow-blue-500/20 cursor-pointer hover:scale-105 transition-transform">
                                <Sparkles className="w-3 h-3 text-white" />
                                <span className="text-xs font-bold text-white">Ask AI</span>
                            </div>
                        </div>
                        <div className="text-sm text-slate-500">Patient: <span className="font-semibold text-slate-900">Sarah Johnson</span></div>
                    </div>

                    <div className="p-6 bg-slate-50 flex-1 overflow-hidden relative">
                        {/* AI Summary Card */}
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-4">
                            <div className="flex items-center gap-2 mb-4 text-purple-600">
                                <Sparkles className="w-4 h-4" />
                                <span className="text-sm font-bold uppercase tracking-wider">AI Generated Insight</span>
                            </div>
                            <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
                                <p>
                                    <span className="font-semibold">General Coverage:</span> Sarah has active PPO coverage with Delta Dental. Her annual maximum is <span className="font-bold text-slate-900">$2,000</span>, with <span className="font-bold text-slate-900">$1,450</span> remaining.
                                </p>
                                <p>
                                    <span className="font-semibold">Today's Visit:</span> For the scheduled Crown (D2740) on tooth #30, the plan pays <span className="font-bold text-green-600">50%</span>.
                                </p>
                                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 flex gap-3 text-amber-800">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span>
                                        <strong>Note:</strong> There is a 5-year replacement clause. Tooth #30 was last treated on 04/12/2018, so it is <span className="underline">eligible</span> for replacement.
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Raw Data Preview (Blurred/Background) */}
                        <div className="opacity-50 blur-[1px] select-none pointer-events-none">
                            <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-2">
                                <div className="h-4 bg-slate-100 rounded w-3/4" />
                                <div className="h-4 bg-slate-100 rounded w-1/2" />
                                <div className="h-4 bg-slate-100 rounded w-full" />
                                <div className="h-4 bg-slate-100 rounded w-2/3" />
                            </div>
                        </div>

                        {/* Ask AI Flyout */}
                        <motion.div 
                            initial={{ y: "100%", opacity: 0 }}
                            animate={isActive ? { y: 0, opacity: 1 } : { y: "100%", opacity: 0 }}
                            transition={{ delay: 0.8, type: "spring", damping: 20, stiffness: 100 }}
                            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-[0_-10px_40px_rgba(0,0,0,0.1)] border-t border-slate-200 max-h-[450px] flex flex-col"
                        >
                            {/* Flyout Header */}
                            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white rounded-t-2xl">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600">
                                        <Bot className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-slate-900 text-sm">AI Assistant</h4>
                                        <p className="text-xs text-slate-500">Ask about eligibility details</p>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400">
                                        <ChevronDown className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400">
                                        <X className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>

                            {/* Flyout Content */}
                            <div className="p-6 overflow-y-auto bg-slate-50/50 flex-1">
                                <div className="flex flex-col items-center justify-center text-center space-y-6 mt-4">
                                    <div className="w-12 h-12 bg-white rounded-xl shadow-sm border border-slate-200 flex items-center justify-center text-indigo-500">
                                        <Sparkles className="w-6 h-6" />
                                    </div>
                                    
                                    <div className="space-y-1">
                                        <h3 className="font-bold text-slate-900">How can I help you?</h3>
                                        <p className="text-sm text-slate-500">Ask me anything about this eligibility check</p>
                                    </div>

                                    {/* Suggested Questions */}
                                    <div className="w-full space-y-2">
                                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-left">Suggested Questions</div>
                                        {[
                                            "What's the patient's remaining deductible?",
                                            "Are there any prior authorization requirements?",
                                            "What's the copay for this service?",
                                            "When does the coverage expire?"
                                        ].map((q, i) => (
                                            <button 
                                                key={i}
                                                className="w-full text-left p-3 rounded-xl bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all text-sm text-slate-700 font-medium"
                                            >
                                                {q}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Input Area */}
                            <div className="p-4 bg-white border-t border-slate-200">
                                <div className="relative">
                                    <input 
                                        type="text" 
                                        placeholder="Message AI Assistant..." 
                                        className="w-full pl-4 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                                    />
                                    <button className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-slate-200 text-slate-400 rounded-lg flex items-center justify-center hover:bg-indigo-600 hover:text-white transition-colors">
                                        <Send className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            </div>
        </div>
    </div>
  );
};

export default InsuranceCopilotSlide;
